/**
 * Reading the JWT we keep in localStorage.
 *
 * The route guards used to check only that a token EXISTS. Tokens last 8 hours
 * (ACCESS_TOKEN_EXPIRE_MINUTES = 480), so a stale one waved you straight through: the page
 * rendered, its first API call returned 401, and authFetch bounced you to login. Decoding `exp`
 * here turns that flash into a clean redirect.
 *
 * This is an OPTIMISATION, never the security boundary. The backend validates every token on
 * every request, and authFetch's 401 handler remains the backstop for a token that is rejected
 * for any other reason (revoked, signed with a rotated secret, malformed server-side).
 */

/** Tolerance for a client clock running ahead of the server, in seconds. */
const CLOCK_SKEW_SECONDS = 60;

/**
 * Decode a JWT payload, or null if it isn't readable.
 *
 * JWT payloads are base64URL ('-' and '_', no padding), which plain atob() rejects — the old
 * inline decoder in AdminRoute could throw on a perfectly good token and silently report "no
 * role". Translate the alphabet and restore padding first.
 */
export function decodeToken(token) {
  const raw = token === undefined ? localStorage.getItem("token") : token;
  if (!raw) return null;
  const segment = raw.split(".")[1];
  if (!segment) return null;
  try {
    const base64 = segment.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    return JSON.parse(atob(padded));
  } catch (e) {
    return null;
  }
}

/**
 * Is the stored token expired?
 *
 * A token with no `exp` counts as NOT expired: we can't prove it's stale, and the server is the
 * authority. Same for an unreadable one — let the request fail and the 401 handler deal with it,
 * rather than logging someone out over a parsing quirk.
 */
export function isTokenExpired(token) {
  const payload = decodeToken(token);
  if (!payload || typeof payload.exp !== "number") return false;
  return payload.exp < Date.now() / 1000 - CLOCK_SKEW_SECONDS;
}

/** A token that exists and hasn't visibly expired, else null. */
export function getUsableToken(token) {
  const raw = token === undefined ? localStorage.getItem("token") : token;
  if (!raw) return null;
  return isTokenExpired(raw) ? null : raw;
}

/** Role claim from the stored token, or null. */
export function getRole(token) {
  const payload = decodeToken(token);
  return payload ? payload.role || null : null;
}
